import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import LinkedinSVG from '@jobcase/react_pattern_library/components/SVGIcon/icons/LinkedinBrandsSVG';
import SVGIcon from '@jobcase/react_pattern_library/components/SVGIcon';
import Typography from '@jobcase/react_pattern_library/components/Typography/Typography';
import styles from '@jobcase/react_pattern_library/styles/rebrand';

import authenticationActions from '../../redux/nodes/authentication/actions';

import { getArrivalKey } from '../../utils/cookie';
import config from '../../config';

const SCOPE = 'r_liteprofile%20r_emailaddress';
const REDIRECT_URI = `${config.jobcaseUrl}/auth/linkedin`;
const STATE = uuidv4();

const ButtonWrapper = styled.button`
  /* Max width defined by limitations of google log in button */
  background: ${styles.color.jobcaseWhiteColor};
  border-radius: ${styles.spacing.borderRadius}px;
  border: 1px solid ${({ theme }) => (theme?.color?.borderColor)};
  cursor: pointer;
  height: 38px;
  padding: 0 ${styles.spacing.extraExtraTiny}px;
  position: relative;
  text-align: center;
  width: 250px;

  svg {
    left: 4px;
    position: absolute;
  }

  > div {
    cursor: pointer;
  }

  @media (min-width: ${styles.breakpoint.md}) {
    width: 336px;
  }

  :hover {
    background: ${styles.color.shimmerTertiaryColor};

    > div {
      cursor: pointer;
    }
  }

  :active {
    background: ${({ theme }) => (theme?.color?.shimmerPrimary)};
  }
`;

const LinkedInOAuth = ({ onLoginSuccess }) => {
  const dispatch = useDispatch();

  const handlePostMessage = async (event) => {
    if (event.data.type === 'linkedinCode') {
      const { code, state } = event.data;
      if (!code || state !== STATE) {
        dispatch(authenticationActions.setLoginError('There was an error logging in with LinkedIn. Please try again.'));
      } else {
        const loggedIn = await dispatch(authenticationActions.linkedInSignIn(code, getArrivalKey(), REDIRECT_URI));
        if (loggedIn && onLoginSuccess) {
          onLoginSuccess('LinkedIn');
        }
      }
    }
  };

  useEffect(() => {
    window.addEventListener('message', handlePostMessage);

    return () => window.removeEventListener('message', handlePostMessage);
  }, []);

  const onLinkedInButtonClick = () => {
    const oauthUrl = 'https://www.linkedin.com/oauth/v2/authorization?response_type=code'
      + `&client_id=${config.linkedInClientId}&scope=${SCOPE}&redirect_uri=${REDIRECT_URI}&state=${STATE}`;
    const width = 450;
    const height = 730;
    const left = window.screen.width / 2 - width / 2;
    const top = window.screen.height / 2 - height / 2;
    window.open(
      oauthUrl,
      'Linkedin',
      `menubar=no,location=no,resizable=no,scrollbars=no,status=no, width=${
        width
      }, height=${
        height
      }, top=${
        top
      }, left=${
        left}`,
    );
  };

  return (
    <ButtonWrapper
      onClick={onLinkedInButtonClick}
      className="linkedin-sign-in"
    >
      <SVGIcon svgChild={LinkedinSVG} size={SVGIcon.ICON_SIZE.X2} color="#0077B5" />
      <Typography>
        Continue with LinkedIn
      </Typography>
    </ButtonWrapper>
  );
};

LinkedInOAuth.propTypes = {
  /**
   * Callback fired on successful login to perform side effects
   */
  onLoginSuccess: PropTypes.func,
};

LinkedInOAuth.defaultProps = {
  onLoginSuccess: null,
};

export default LinkedInOAuth;
